import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { Check } from 'lucide-react';

type StepData = {
  label: string;
  description?: string;
};

type StepStatus = 'done' | 'in-progress' | 'upcoming';

type StepperSize = 'default' | 'lg';

type StepperLabelPlacement = 'below' | 'inline';

type StepClickHandler = (index: number, step: StepData) => void;

type StepperProps = ComponentProps<'div'> & {
  readonly steps: StepData[];
  readonly activeStep?: number;
  readonly orientation?: 'horizontal' | 'vertical';
  readonly showIcon?: boolean;
  readonly showConnector?: boolean;
  readonly size?: StepperSize;
  readonly labelPlacement?: StepperLabelPlacement;
  readonly onStepClick?: StepClickHandler;
};

const STEP_ARIA_SUFFIX: Record<StepStatus, string> = {
  done: ', completed',
  'in-progress': ', current step',
  upcoming: ', upcoming',
};

function stepStatusFor(index: number, activeStep: number): StepStatus {
  if (index < activeStep) return 'done';
  if (index === activeStep) return 'in-progress';
  return 'upcoming';
}

function buildStepAriaLabel(index: number, total: number, step: StepData, status: StepStatus): string {
  const suffix = STEP_ARIA_SUFFIX[status];
  return `Step ${index + 1} of ${total}: ${step.label}${suffix}`;
}

const indicatorVariants = cva('flex shrink-0 items-center justify-center rounded-full transition-colors duration-200', {
  variants: {
    status: {
      done: 'bg-background border-2 border-primary text-primary',
      'in-progress': 'bg-primary text-primary-foreground',
      upcoming: 'bg-background border border-ring text-secondary',
    },
    showIcon: {
      true: 'size-6',
      false: 'size-4',
    },
  },
  defaultVariants: {
    status: 'upcoming',
    showIcon: true,
  },
});

const labelVariants = cva('', {
  variants: {
    size: {
      default: 'text-sm',
      lg: 'text-lg leading-6',
    },
  },
  defaultVariants: { size: 'default' },
});

const descriptionVariants = cva('', {
  variants: {
    size: {
      default: 'text-xs text-secondary',
      lg: 'text-base text-secondary',
    },
  },
  defaultVariants: { size: 'default' },
});

function StepIndicator({
  status,
  stepNumber,
  showIcon,
}: {
  status: StepStatus;
  stepNumber: number;
  showIcon: boolean;
}) {
  return (
    <div data-slot="stepper-indicator" data-status={status} className={cn(indicatorVariants({ status, showIcon }))}>
      {showIcon && status === 'done' && <Check className="size-3.5" strokeWidth={2.5} />}
      {showIcon && status !== 'done' && (
        <span className={cn('text-xs leading-none', status === 'in-progress' && 'font-semibold')}>{stepNumber}</span>
      )}
    </div>
  );
}

function StepConnector({ complete, orientation }: { complete: boolean; orientation: 'horizontal' | 'vertical' }) {
  const isHorizontal = orientation === 'horizontal';

  if (isHorizontal) {
    return (
      <div data-slot="stepper-connector" className="relative h-0.5 min-w-4 flex-1">
        {/* Inset wrapper creates gap between circle edge and line ends */}
        <div className="absolute inset-x-4 inset-y-0">
          {/* Track — always visible (dashed, wider spacing than border-dashed default) */}
          <div
            className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, var(--border) 0, var(--border) 4px, transparent 4px, transparent 10px)',
            }}
          />
          {/* Fill — animatable */}
          <div
            className={cn(
              'absolute top-0 left-0 h-full bg-primary transition-[width] duration-[400ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
              complete ? 'w-full' : 'w-0',
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-slot="stepper-connector" className="relative min-h-6 w-0.5 flex-1">
      {/* Track — always visible (dashed) */}
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 border-l border-dashed border-border" />
      {/* Fill — animatable */}
      <div
        className={cn(
          'absolute top-0 left-0 w-0.5 bg-primary transition-[height] duration-[400ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
          complete ? 'h-full' : 'h-0',
        )}
      />
    </div>
  );
}

// Inner wrapper for each step's indicator+label. Renders as a <button> when
// onClick is provided; otherwise a plain presentational <div> — non-interactive
// steps stay out of the tab order so keyboard users don't land on dead stops.
// The accessible name lives on the parent <li> in every case (a <div> with
// implicit role="generic" doesn't support naming, so aria-label here would be
// dropped by screen readers).
function StepInteractive({
  onClick,
  ariaLabel,
  className,
  children,
}: {
  onClick?: () => void;
  ariaLabel: string;
  className: string;
  children: ReactNode;
}) {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(
          className,
          'cursor-pointer text-left rounded outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        {children}
      </button>
    );
  }

  return <div className={className}>{children}</div>;
}

function HorizontalStepper({
  steps,
  activeStep,
  showIcon,
  size,
  onStepClick,
}: {
  steps: StepData[];
  activeStep: number;
  showIcon: boolean;
  size: StepperSize;
  onStepClick?: StepClickHandler;
}) {
  // Layout: a relative container with two layers.
  //
  // Layer 1 (absolute): single connector lines between each pair of steps,
  // positioned to span from circle center to circle center. Each is one DOM
  // element so the animation is a single left-to-right fill.
  //
  // Layer 2 (flex): equal-width columns with indicator + label stacked.
  // The indicator and label are always vertically aligned because they
  // share the same flex column.
  //
  // The indicator size determines vertical positioning of connectors.
  const indicatorSize = showIcon ? 24 : 16; // size-6 = 24px, size-4 = 16px

  return (
    <div className="relative">
      {/* Connector lines (absolutely positioned, decorative) */}
      <div aria-hidden="true" className="absolute inset-x-0" style={{ top: indicatorSize / 2 }}>
        <div className="flex">
          {steps.map((_, i) => {
            if (i === steps.length - 1) return null;
            return (
              <div
                key={i}
                className="flex-1"
                style={{
                  ...(i === 0 ? { marginLeft: `${100 / (steps.length * 2)}%` } : {}),
                  ...(i === steps.length - 2 ? { marginRight: `${100 / (steps.length * 2)}%` } : {}),
                }}
              >
                <StepConnector complete={i < activeStep} orientation="horizontal" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Step columns (semantic list) */}
      <ol className="relative flex list-none p-0 m-0">
        {steps.map((step, i) => {
          const status = stepStatusFor(i, activeStep);
          const isClickable = onStepClick !== undefined && status === 'done';

          const ariaLabel = buildStepAriaLabel(i, steps.length, step, status);

          return (
            <li
              key={i}
              data-slot="stepper-step"
              data-status={status}
              aria-current={status === 'in-progress' ? 'step' : undefined}
              aria-label={ariaLabel}
              className="min-w-0 flex-1"
            >
              <StepInteractive
                onClick={isClickable ? () => onStepClick(i, step) : undefined}
                ariaLabel={ariaLabel}
                className="flex w-full flex-col items-center gap-2"
              >
                <StepIndicator status={status} stepNumber={i + 1} showIcon={showIcon} />
                <div className="flex flex-col items-center gap-0.5">
                  <span
                    data-slot="stepper-label"
                    className={cn(
                      'text-center text-sm text-dark',
                      status === 'in-progress' ? 'font-semibold' : 'font-normal',
                    )}
                  >
                    {step.label}
                  </span>
                  {step.description && (
                    <span data-slot="stepper-description" className={cn(descriptionVariants({ size }), 'text-center')}>
                      {step.description}
                    </span>
                  )}
                </div>
              </StepInteractive>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function HorizontalStepperInline({
  steps,
  activeStep,
  showIcon,
  onStepClick,
}: {
  steps: StepData[];
  activeStep: number;
  showIcon: boolean;
  onStepClick?: StepClickHandler;
}) {
  return (
    <ol className="flex items-center list-none p-0 m-0 gap-2">
      {steps.flatMap((step, i) => {
        const status = stepStatusFor(i, activeStep);
        const isLast = i === steps.length - 1;
        const isClickable = onStepClick !== undefined && status === 'done';
        const ariaLabel = buildStepAriaLabel(i, steps.length, step, status);
        return [
          <li
            key={`step-${i}`}
            data-slot="stepper-step"
            data-status={status}
            aria-current={status === 'in-progress' ? 'step' : undefined}
            aria-label={ariaLabel}
            className="shrink-0"
          >
            <StepInteractive
              onClick={isClickable ? () => onStepClick(i, step) : undefined}
              ariaLabel={ariaLabel}
              className="flex items-center gap-2"
            >
              <StepIndicator status={status} stepNumber={i + 1} showIcon={showIcon} />
              <span
                data-slot="stepper-label"
                className={cn('text-sm text-dark', status === 'in-progress' ? 'font-semibold' : 'font-normal')}
              >
                {step.label}
              </span>
            </StepInteractive>
          </li>,
          isLast ? null : (
            <li key={`connector-${i}`} role="presentation" aria-hidden="true" className="flex-1 min-w-4">
              <StepConnector complete={i < activeStep} orientation="horizontal" />
            </li>
          ),
        ];
      })}
    </ol>
  );
}

function VerticalStepper({
  steps,
  activeStep,
  showIcon,
  showConnector,
  size,
  onStepClick,
}: {
  steps: StepData[];
  activeStep: number;
  showIcon: boolean;
  showConnector: boolean;
  size: StepperSize;
  onStepClick?: StepClickHandler;
}) {
  return (
    <ol className="flex flex-col list-none p-0 m-0">
      {steps.map((step, i) => {
        const status = stepStatusFor(i, activeStep);
        const isLast = i === steps.length - 1;
        const isClickable = onStepClick !== undefined && status === 'done';

        const ariaLabel = buildStepAriaLabel(i, steps.length, step, status);

        return (
          <li
            key={i}
            data-slot="stepper-step"
            data-status={status}
            aria-current={status === 'in-progress' ? 'step' : undefined}
            aria-label={ariaLabel}
            className={cn(size === 'lg' ? 'pb-3' : 'pb-0')}
          >
            <StepInteractive
              onClick={isClickable ? () => onStepClick(i, step) : undefined}
              ariaLabel={ariaLabel}
              className="flex w-full gap-3"
            >
              <div className="flex flex-col items-center">
                <StepIndicator status={status} stepNumber={i + 1} showIcon={showIcon} />
                {showConnector && !isLast && <StepConnector complete={i < activeStep} orientation="vertical" />}
              </div>

              <div className={cn('flex flex-col', size === 'lg' ? 'gap-1' : 'gap-0.5', !showConnector && 'pb-3')}>
                <span
                  data-slot="stepper-label"
                  className={cn(
                    labelVariants({ size }),
                    'text-dark',
                    status === 'in-progress' ? 'font-semibold' : 'font-normal',
                  )}
                >
                  {step.label}
                </span>
                {step.description && (
                  <span data-slot="stepper-description" className={descriptionVariants({ size })}>
                    {step.description}
                  </span>
                )}
              </div>
            </StepInteractive>
          </li>
        );
      })}
    </ol>
  );
}

function Stepper({
  className,
  steps,
  activeStep = 0,
  orientation = 'horizontal',
  showIcon = true,
  showConnector = true,
  size = 'default',
  labelPlacement = 'below',
  onStepClick,
  ...props
}: StepperProps) {
  function renderBody() {
    if (orientation === 'vertical') {
      return (
        <VerticalStepper
          steps={steps}
          activeStep={activeStep}
          showIcon={showIcon}
          showConnector={showConnector}
          size={size}
          onStepClick={onStepClick}
        />
      );
    }
    if (labelPlacement === 'inline') {
      return (
        <HorizontalStepperInline steps={steps} activeStep={activeStep} showIcon={showIcon} onStepClick={onStepClick} />
      );
    }
    return (
      <HorizontalStepper
        steps={steps}
        activeStep={activeStep}
        showIcon={showIcon}
        size={size}
        onStepClick={onStepClick}
      />
    );
  }

  return (
    <div
      role="group"
      aria-label="Progress"
      data-slot="stepper"
      data-orientation={orientation}
      className={cn(className)}
      {...props}
    >
      {renderBody()}
    </div>
  );
}

export { Stepper };
export type { StepData, StepperProps };
